import {useQueries} from '@tanstack/react-query';
import {FC, useContext, useMemo} from 'react';
import {Link} from 'react-router-dom';
import {Message, Table} from 'semantic-ui-react';
import {map, find, some, omit, isEmpty, isMatch, size} from 'lodash';
import {
  Checkbox, DataFilter,
  DataFilteringLayout, DataTable, DateFromNow, Value,
  interpolateRoute, parseTimestamp, request, stringifyState,
  stringFilterer, objectFilterer
} from 'apstra-ui-common';
import {useCustomCompareEffect} from 'react-use';

import generateProbeURI from '../generateProbeURI';
import {anomalyPropertiesFilterRenderer} from './AnomalyPropertiesFilterInput';
import getCellColor from '../getCellColor';
import AnomalyValues from './AnomalyValues';
import IBAContext from '../IBAContext';
import {apiQueryOptions} from '../../apiQueryOptions';
import {useSearchStringFilters} from '../../useSearchStringFilters';
import userStore from '../../userStore';

import './AnomalyList.less';

function formatProbeLink({blueprintId, probeId, probes}) {
  const probe = find(probes, {id: probeId});
  return probe ? <Link to={generateProbeURI({blueprintId, probeId})}>{probe.label}</Link> : probeId;
}

function formatStageLink({blueprintId, probeId, stageName, probes}) {
  const probe = find(probes, {id: probeId});
  return probe
    ? <Link to={generateProbeURI({blueprintId, probeId, stageName})}>{stageName}</Link>
    : stageName;
}

function getProbeLabel({probeId, probes}) {
  const probe = find(probes, {id: probeId});
  return probe ? probe.label : null;
}

const nonGroupedTableSchema = [
  {
    name: 'probe',
    label: 'Probe',
    value: ['identity', 'probe_id'],
    formatter: ({value: probeId, params: {blueprintId, probes}}) => formatProbeLink({blueprintId, probeId, probes}),
    sortable: true,
  },
  {
    name: 'stage',
    label: 'Stage',
    value: ['identity'],
    formatter: ({value: {probe_id: probeId, stage_name: stageName}, params: {blueprintId, probes}}) =>
      formatStageLink({blueprintId, probeId, stageName, probes}),
    sortable: true,
  },
  {
    name: 'properties',
    label: 'Properties',
    value: ['identity', 'properties'],
    formatter: ({value}) =>
      <Table definition basic='very' collapsing size='small'>
        <Table.Body>
          {map(value, ({key, value}) =>
            <Table.Row key={key}>
              <Table.Cell>{key}</Table.Cell>
              <Table.Cell><Value value={value} /></Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
  },
  {
    name: 'values',
    label: 'Values',
    formatter: ({item}) => <AnomalyValues anomaly={item} />,
  },
  {
    name: 'updated',
    label: 'Updated',
    value: ({item}) => parseTimestamp(item.last_modified_at),
    formatter: ({value}) => <DateFromNow date={value} />,
    sortable: true,
  },
];

const groupedTableSchema = [
  {
    name: 'probe',
    label: 'Probe',
    value: ['probe_id'],
    formatter: ({value: probeId, params: {blueprintId, probes}}) => formatProbeLink({blueprintId, probeId, probes}),
    sortable: true,
  },
  {
    name: 'stage',
    label: 'Stage',
    value: ['stage_name'],
    formatter: ({item: {probe_id: probeId, stage_name: stageName}, params: {blueprintId, probes}}) =>
      formatStageLink({blueprintId, probeId, stageName, probes}),
    sortable: true,
  },
  {
    name: 'anomalies',
    label: 'Anomalies',
    value: ({item: {items, anomalies}}) => [items ? anomalies / items : 0, anomalies],
    formatter: ({
      item: {probe_id: probeId, stage_name: stageName, items, anomalies},
      params: {probes, blueprintId}
    }) => {
      const probe = find(probes, {id: probeId});
      const filters: {stage_name: string, probe_label?: string} = {stage_name: stageName};
      if (probe) filters.probe_label = probe.label;
      const stringifiedFilters = stringifyState({filters});
      return (
        <Link
          style={{color: getCellColor({anomalies, items}).foreground}}
          to={`/blueprints/${blueprintId}/analytics/anomalies?probe-anomalies-filter=${stringifiedFilters}`}
        >
          {`${anomalies} / ${items}`}
        </Link>
      );
    },
    sortable: true,
  },
];

const nonGroupedFilterers = [
  stringFilterer(
    ({identity: {probe_id: probeId}}, {probes}) => getProbeLabel({probeId, probes}),
    ['probe_label']
  ),
  stringFilterer(
    ['identity', 'stage_name'],
    ['stage_name']
  ),
  objectFilterer(
    ['identity', 'properties'],
    ['properties', 'properties'],
    ['properties', 'strict'] as any,
    (itemValue, value, key) => some(itemValue, (propertyPair) => isMatch(propertyPair, {key, value}))
  ),
];

const groupedFilterers = [
  stringFilterer(
    ({probe_id: probeId}, {probes}) => getProbeLabel({probeId, probes}),
    ['probe_label']
  ),
  stringFilterer(
    ['stage_name'],
    ['stage_name']
  ),
];

const commonSearchBoxSchema = [
  {
    name: 'probe_label',
    schema: {
      type: 'string',
      title: 'Probe Label',
    }
  },
  {
    name: 'stage_name',
    schema: {
      type: 'string',
      title: 'Stage Name',
    }
  },
];

const nonGroupedSearchBoxSchema = [
  ...commonSearchBoxSchema,
  {
    name: 'properties',
    schema: {
      type: 'object',
      title: 'Properties',
      default: {properties: {}, strict: true},
    }
  },
];

const groupedSearchBoxSchema = [
  ...commonSearchBoxSchema,
  {
    name: 'showStagesWithoutAnomalies',
    schema: {
      type: 'boolean',
      title: 'Show stages without anomalies',
    }
  },
];

type AnomalyListProps = {
  grouped?: boolean;
  toggleGrouped?: () => void;
  userStoreKey?: string;
}

const AnomalyList: FC<AnomalyListProps> = ({
  grouped,
  toggleGrouped,
  userStoreKey = 'anomalyList'
}) => {
  const {blueprintId, routes} = useContext(IBAContext);

  const defaultPageSize = userStore.getStoreValue([userStoreKey, 'pageSize']);
  const {
    activePage, pageSize, updatePagination, filters, updateFilters,
    sorting, updateSorting
  } = useSearchStringFilters<{
    showStagesWithoutAnomalies?: boolean,
  }>({
    stateQueryParam: 'probe-anomalies-filter',
    defaultPageSize: defaultPageSize,
    defaultSorting: {probe: 'asc'},
    setUserStoreProps: userStore.setStoreValueFn(userStoreKey),
  });

  useCustomCompareEffect(() => {
    updateFilters(omit(filters, 'properties'));
  }, [grouped, filters, updateFilters], ([prevGrouped], [nextGrouped]) => prevGrouped === nextGrouped);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (grouped) {
      if (!filters.showStagesWithoutAnomalies) params.append('filter', 'anomalies>0');
    } else {
      params.append('anomaly_type', 'probe');
    }
    return params.toString();
  }, [grouped, filters.showStagesWithoutAnomalies]);

  const {probes, anomalousStages, anomalies, loaderVisible, fetchDataError} = useQueries({
    queries: [{
      ...apiQueryOptions.blueprintProbes(blueprintId),
      queryFn: ({signal}) => request(interpolateRoute(routes.probeList, {blueprintId}), {signal}),
    }, {
      ...apiQueryOptions.anomalousStageList(blueprintId, queryParams),
      queryFn: ({signal}) => request(interpolateRoute(routes.anomalousStageList, {blueprintId}), {queryParams, signal}),
      enabled: grouped,
    }, {
      ...apiQueryOptions.anomalyList(blueprintId),
      queryFn: ({signal}) =>
        request(interpolateRoute(routes.anomalyList, {blueprintId}), {queryParams: {anomaly_type: 'probe'}, signal}),
      enabled: !grouped,
    }],
    combine: (results) => {
      return {
        probes: results[0].data?.items,
        anomalousStages: grouped ? results[1].data?.items : [],
        anomalies: grouped ? [] : results[2].data?.items,
        loaderVisible: some(results, (r) => r.isLoading),
        fetchDataError: find(results, (r) => !!r.error)?.error,
      };
    }
  });

  const allItems = loaderVisible ? [] : grouped ? anomalousStages : anomalies;
  const tableSchema = grouped ? groupedTableSchema : nonGroupedTableSchema;
  const searchBoxSchema = grouped ? groupedSearchBoxSchema : nonGroupedSearchBoxSchema;
  const filterers = grouped ? groupedFilterers : nonGroupedFilterers;
  return (
    <DataFilter
      items={allItems}
      schema={tableSchema}
      params={{probes}}
      activePage={activePage}
      pageSize={pageSize}
      filterers={filterers}
      filters={filters}
      sorting={sorting}
    >
      {({items, totalCount}) =>
        <DataFilteringLayout
          loaderVisible={loaderVisible}
          fetchDataError={fetchDataError}
          hideActionsAndPagination={!size(allItems) && isEmpty(filters)}
          paginationProps={{
            totalCount,
            activePage,
            pageSize,
            onChange: updatePagination
          }}
          searchProps={{
            filters,
            schema: searchBoxSchema,
            renderers: [anomalyPropertiesFilterRenderer],
            onChange: updateFilters
          }}
          extraContent={{
            buttonsPanel: (
              <div className='anomalies-grouping'>
                <Checkbox
                  label='Group by stage'
                  checked={grouped}
                  onChange={toggleGrouped}
                />
              </div>
            )
          }}
        >
          {
            isEmpty(filters) && !items.length ?
              <Message
                success
                icon='check circle'
                header='No anomalies!'
              />
            :
              <DataTable
                items={items}
                schema={tableSchema}
                params={{blueprintId, probes}}
                sorting={sorting}
                updateSorting={updateSorting}
                className='anomaly-list'
                noItemsMessage='No anomalies'
                getCellProps={
                  ({name, item: {items, anomalies}}) => name === 'anomalies' && {
                    className: 'anomalies',
                    style: {backgroundColor: getCellColor({anomalies, items}).background},
                  }
                }
              />
          }
        </DataFilteringLayout>
      }
    </DataFilter>
  );
};

export default AnomalyList;
